import { formatUsd } from "../formatUsd.js";
import { ViewModel } from "../mvvm/ViewModel.js";
export class OwnerOrdersViewModel extends ViewModel {
    client;
    /** IANA zone for createdAt display; empty => browser-local (never force UTC). */
    displayTimeZone = "";
    constructor(client) {
        super({
            orders: [],
            selected: null,
            loading: false,
            error: null,
            confirmingRefund: false,
            refunding: false,
            refundCents: 0,
        });
        this.client = client;
    }
    /** Closes #574: restaurant wall clock from SiteContent.timezone. */
    setDisplayTimeZone(tz) {
        this.displayTimeZone = tz.trim();
    }
    async load() {
        await this.run(async () => {
            const orders = await this.client.listMyOrders();
            this.setState({ orders });
        }, "Could not load orders.");
    }
    async select(id) {
        this.setState({ confirmingRefund: false });
        await this.run(async () => {
            const selected = await this.client.getMyOrder(id);
            this.setState({ selected });
        }, "Could not load order.");
    }
    clearSelected() {
        this.setState({ selected: null, confirmingRefund: false });
    }
    /** First click on Refund — show confirm UI, do not call API yet. */
    askRefundConfirm() {
        const selected = this.state.selected;
        if (!selected || !this.canRefundSelected())
            return;
        this.setState({
            confirmingRefund: true,
            error: null,
            refundCents: this.remainingRefundCents(selected),
        });
    }
    setRefundCents(cents) {
        if (!Number.isFinite(cents))
            return;
        this.setState({ refundCents: Math.max(0, Math.round(cents)) });
    }
    remainingRefundCents(o) {
        return Math.max(0, o.totalCents - (o.refundedCents ?? 0));
    }
    cancelRefundConfirm() {
        this.setState({ confirmingRefund: false });
    }
    canRefundSelected() {
        const o = this.state.selected;
        if (!o)
            return false;
        // Status wins — never offer refund when already refunded (even if refundable flag stale).
        if (o.status === "refunded")
            return false;
        if (typeof o.refundable === "boolean")
            return o.refundable;
        return !!o.paid;
    }
    isRefunded(o) {
        return o?.status === "refunded";
    }
    async confirmRefund() {
        const current = this.state.selected;
        const id = current?.id;
        if (!id || !current || !this.state.confirmingRefund)
            return;
        const remaining = this.remainingRefundCents(current);
        const amount = this.state.refundCents;
        if (amount <= 0 || amount > remaining) {
            this.setState({ error: "Enter a refund up to the remaining balance." });
            return;
        }
        await this.run(async () => {
            const selected = amount < remaining
                ? await this.client.refundMyOrder(id, amount)
                : await this.client.refundMyOrder(id);
            const orders = this.state.orders.map((o) => (o.id === selected.id ? selected : o));
            this.setState({
                selected,
                orders,
                confirmingRefund: false,
            });
        }, "Refund failed.", { flag: "refunding" });
    }
    formatPrice(cents) {
        return formatUsd(cents);
    }
    /** List-row calendar date from order `createdAt` (store TZ, else browser-local). */
    formatDate(iso) {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime()))
            return "";
        return new Intl.DateTimeFormat("en-US", this.clockOpts({
            month: "short",
            day: "numeric",
            year: "numeric",
        })).format(d);
    }
    /** List-row clock time from order `createdAt` (store TZ, else browser-local). */
    formatTime(iso) {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime()))
            return "";
        return new Intl.DateTimeFormat("en-US", this.clockOpts({
            hour: "numeric",
            minute: "2-digit",
        })).format(d);
    }
    clockOpts(base) {
        return this.displayTimeZone ? { ...base, timeZone: this.displayTimeZone } : base;
    }
    formatLineTotal(quantity, priceCents) {
        return this.formatPrice(quantity * priceCents);
    }
}
//# sourceMappingURL=OwnerOrdersViewModel.js.map